import { FC, ReactElement } from "react";
//#region types
import { UserPayoutDetailsProps } from "models/propTypes/wintSalaryPropTypes";
import { TranslationKeys } from "translations";
import { Currency } from "translations/translationKeys";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { InfoTooltip } from "components/common";
//#endregion
import { buildPayoutLines, PayoutLine } from "./payoutLines";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

interface RowProps {
  label: string;
  labelClass: string;
  labelSuffix?: ReactElement;
  value: string;
  valueClass: string;
  alignItems?: "center" | "flex-end";
}

const Row: FC<RowProps> = ({ label, labelClass, labelSuffix, value, valueClass, alignItems = "center" }) => (
  <Grid item xs={12} className={cssExports.detailRow}>
    <Grid container justifyContent="space-between" alignItems={alignItems} wrap="nowrap" columnGap={2}>
      {labelSuffix ? (
        <Grid item container alignItems="center" columnGap={0.5} width="auto">
          <Grid item className={labelClass}>{label}</Grid>
          {labelSuffix}
        </Grid>
      ) : (
        <Grid item className={labelClass}>{label}</Grid>
      )}
      <Grid item className={valueClass}>{value}</Grid>
    </Grid>
  </Grid>
);

const UserPayoutDetails: FC<UserPayoutDetailsProps> = (props) => {
  const { regularPayoutAmount, taxAmount, taxTable, earlyPayoutsAmount, regularPayoutDate, payouts, unscheduledPayoutLabel } =
    props;
  const formatCurrency = (amount: number) => LocalizationHelperInstance.getFormattedCurrency(amount, Currency.SEK);

  const earlyPayoutsTotal = earlyPayoutsAmount ?? 0;      // "Tidiga utbetalningar" — before payday, any state except won't-be-paid
  const regularLineAmount = regularPayoutAmount;          // on or after the payday, broken into lines below
  const totalNet = regularLineAmount + earlyPayoutsTotal; // "Total nettolön"
  const showEarlyLine = earlyPayoutsTotal > 0;

  const payoutLines = buildPayoutLines(payouts, regularLineAmount, regularPayoutDate);

  const payoutLineLabel = (line: PayoutLine) => {
    if (!line.date) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.UserPayoutPaidOut);
    }
    if (line.unscheduled && unscheduledPayoutLabel) {
      return unscheduledPayoutLabel;
    }
    return LocalizationHelperInstance.getFormattedMessage(
      line.settled
        ? TranslationKeys.SalaryDeviation.UserPayoutSettledOnDate
        : TranslationKeys.SalaryDeviation.UserPayoutPaidOnDate,
      undefined,
      { date: LocalizationHelperInstance.getFormattedDate(line.date) }
    );
  };

  return (
    <Grid container className={cssExports.userPayoutDetails}>
      <Row
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.UserPayoutTotalNetSalary)}
        labelClass={cssExports.title}
        value={formatCurrency(totalNet)}
        valueClass={cssExports.netPaymentValue}
        alignItems="flex-end"
      />
      {/* Without a payout date or early payouts the row would just repeat
          "Total nettolön" under a label that reads like a header — skip it. */}
      {(regularPayoutDate || showEarlyLine) &&
        payoutLines.map((line) => (
          <Row
            key={`${line.settled}-${line.date?.getTime() ?? "none"}`}
            label={payoutLineLabel(line)}
            labelClass={cssExports.netPaymentLabel}
            value={formatCurrency(line.amount)}
            valueClass={cssExports.taxTableValue}
          />
        ))}
      {showEarlyLine && (
        <Row
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.UserPayoutEarlyPayouts)}
          labelClass={cssExports.netPaymentLabel}
          value={formatCurrency(earlyPayoutsTotal)}
          valueClass={cssExports.taxTableValue}
        />
      )}
      <Row
        label={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.UserPayoutDetailsTaxTable,
          undefined,
          { taxTable: taxTable?.toString() ?? "" }
        )}
        labelClass={cssExports.netPaymentLabel}
        labelSuffix={
          <InfoTooltip
            text={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.UserPayoutPrivateTaxNote
            )}
            icon={IconDefinitionConstants.WInfo}
          />
        }
        // Avoid "-0 kr" when there's no tax — only negate when the amount is non-zero.
        value={formatCurrency(taxAmount === 0 ? 0 : -taxAmount)}
        valueClass={cssExports.taxTableValue}
      />
    </Grid>
  );
};

export default UserPayoutDetails;
